
import { useCallback } from 'react';
import { AirtableService } from '../api/airtableService';

/**
 * @callback DataService
 * @param {string} linkValue - The value to look up in the external data source.
 * @returns {Promise<object>} A promise that resolves to the linked data.
 */

/**
 * A map of data source types to their corresponding data fetching services.
 * To add a new data source, add a new entry to this object.
 * The key should be a unique string identifying the data source type.
 * The value should be a function that fetches the data.
 *
 * @type {Object.<string, DataService>}
 */
const dataServices = {
    airtable: (linkValue) => AirtableService('Client_Asset_ID', linkValue),
    /**
     * Simple Excel lookup service. Expects an object with the Excel rows and
     * the column to search by. Returns the first row where the column value
     * matches the provided link value.
     *
     * @param {string} linkValue - Value from the asset row used for lookup.
     * @param {{excelData: object[], linkColumn: string}} options - Excel data
     *        and selected link column.
     */
    excel: (linkValue, { excelData = [], linkColumn }) => {
        console.log('Excel service called with:', { linkValue, excelDataCount: excelData.length, linkColumn });
        if (!excelData || !linkColumn || excelData.length === 0) {
            console.log('Excel service: Missing data or column');
            return null;
        }
        const result = excelData.find(row => {
            const cellValue = row[linkColumn];
            const match = cellValue === linkValue || String(cellValue) === String(linkValue);
            if (match) {
                console.log('Excel service: Found match', { cellValue, linkValue, row });
            }
            return match;
        });
        console.log('Excel service result:', result);
        return result || null;
    },
    // To add another data source, such as SQL, you would add a new service here:
    // sql: async (linkValue) => {
    //   const response = await fetch(`/api/sql-data?id=${linkValue}`);
    //   return response.json();
    // }
};


/**
 * CUSTOM HOOK FOR LINKED DATA AND TOGGLE ROW FUNCTIONALITY
 * 
 * This hook manages fetching and displaying linked data from various external
 * sources within Tabulator table rows. It provides toggle functionality that
 * allows users to expand/collapse rows to show additional related data.
 * 
 * SUPPORTED DATA SOURCES:
 * - Airtable: Fetches data using AirtableService with Client_Asset_ID lookup
 * - Excel: Searches through Excel data using configurable column matching
 * - Extensible: New data sources can be added to the dataServices object
 * 
 * TOGGLE ROW FUNCTIONALITY:
 * - Expands/collapses rows by adding/removing subtable rows
 * - Creates formatted tables within expanded rows showing linked data
 * - Handles visual state changes (expanded/collapsed styling)
 * - Provides error handling and "no data" messaging
 * - Uses proper DOM manipulation to span content across all columns
 * 
 * IMPLEMENTATION DETAILS:
 * - Uses Tabulator's addRow API to insert subtable rows
 * - Replaces entire row HTML to achieve proper column spanning
 * - Applies CSS classes for styling (expanded, subtable)
 * - Handles async data fetching with proper error boundaries
 * 
 * @param {string} dataSourceType - The type of data source ('airtable', 'excel', etc.)
 * @param {object} [options] - Configuration options for the data source
 * @param {object[]} [options.excelData] - Excel data array (for Excel source)
 * @param {string} [options.linkColumn] - Column name to match against (for Excel source)
 * @returns {{toggleRow: function}} - Object containing the toggleRow function
 */
export const useLinkedData = (dataSourceType, options = {}) => {
    /**
     * TOGGLE ROW FUNCTION - CORE FUNCTIONALITY
     * 
     * Toggles the expansion state of a table row to show/hide linked data
     * from external sources. This function handles the complete lifecycle
     * of row expansion including data fetching, DOM manipulation, and styling.
     * 
     * EXPANSION PROCESS:
     * 1. Checks current expansion state using CSS classes
     * 2. If expanding: fetches data from configured source
     * 3. Creates formatted subtable with fetched data
     * 4. Inserts subtable row after the clicked row
     * 5. Applies proper styling and CSS classes
     * 
     * COLLAPSE PROCESS:
     * 1. Finds and removes the associated subtable row
     * 2. Updates visual state of the parent row
     * 
     * ERROR HANDLING:
     * - Shows user-friendly messages for no data found
     * - Displays error messages for failed requests
     * - Maintains UI consistency even when data fetching fails
     * 
     * DOM MANIPULATION:
     * - Replaces entire row HTML to achieve proper column spanning
     * - Uses single .subtable-cell div containing formatted table
     * - Applies CSS classes for styling integration
     *
     * @param {import('tabulator-tables').RowComponent} row - The Tabulator row component to toggle
     */
    const toggleRow = useCallback(async (row) => {
        console.log(`toggleRow called for ${dataSourceType}`, { row: row.getData(), options });
        
        const isExpanded = row.getElement().classList.contains('expanded');
        row.getElement().classList.toggle('expanded', !isExpanded);
        row.getElement().classList.toggle('collapsed', isExpanded);

        const nextRow = row.getNextRow();
        if (isExpanded) {
            console.log('Collapsing row - removing subtable');
            if (nextRow && nextRow.getElement().classList.contains('subtable')) {
                nextRow.delete();
            }
            return;
        }

        const linkValue = row.getData().clientAssetId;
        console.log(`Expanding row with linkValue: ${linkValue}`);
        
        const dataService = dataServices[dataSourceType];

        if (!dataService) {
            console.error(`No data service found for type: ${dataSourceType}`);
            return;
        }

        try {
            const data = await dataService(linkValue, options);
            if (!data) {
                console.warn(`No data returned from ${dataSourceType} for link value: ${linkValue}`);
                // Show a message in the expanded row
                const noDataRowData = {
                    id: `no-data-${row.getData().id}`,
                    _isSubtable: true,
                    _noData: true
                };
                
                const newRow = await row.getTable().addRow(noDataRowData, false, row, true);
                if (newRow) {
                    const newRowElement = newRow.getElement();
                    newRowElement.classList.add('subtable');
                    
                    // Replace the entire row content with our message
                    newRowElement.innerHTML = `
                        <div class="tabulator-cell subtable-cell" style="padding: 0; border: none; background-color: #ffffff; width: 100%;">
                            <div style="padding: 20px; text-align: center; color: #888; font-style: italic;">
                                No linked data found for "${linkValue}" in ${dataSourceType}
                            </div>
                        </div>
                    `;
                }
                return;
            }

            console.log(`Data found for ${linkValue}:`, data);
            const headers = Object.keys(data);
            const details = Object.values(data);

            /**
             * SUBTABLE ROW CREATION WITH PROPER COLUMN SPANNING
             * 
             * Creates a new row that displays the linked data in a formatted table.
             * The key innovation here is replacing the entire row HTML to achieve
             * proper column spanning, which Tabulator doesn't handle well with
             * traditional colspan attributes.
             * 
             * Row Structure:
             * - Creates row with metadata for identification
             * - Replaces entire row HTML with single .subtable-cell div
             * - Contains formatted HTML table with headers and data
             * - Applies proper styling for visual integration
             * 
             * This approach ensures the subtable spans the full width of the
             * parent table regardless of the number of columns.
             */
            const newRowData = {
                id: `detail-${row.getData().id}`,
                _isSubtable: true,
                _subtableData: data,
                _headers: headers,
                _details: details
            };

            const newRow = await row.getTable().addRow(newRowData, false, row, true);
            if (newRow) {
                const newRowElement = newRow.getElement();
                newRowElement.classList.add('subtable');
                
                // Replace the entire row content with our custom table
                newRowElement.innerHTML = `
                    <div class="tabulator-cell subtable-cell" style="padding: 0; border: none; background-color: #ffffff; width: 100%;">
                        <table style="width: 100%; border-collapse: collapse;   margin: 5px 0;">
                            <thead>
                                <tr style="background: #f2f2f2;">
                                    ${headers.map(header => `<th style="color: rgba(0,0,0,0.5); border: 1px solid #ddd; padding: 8px; font-weight: bold;">${header}</th>`).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    ${details.map(detail => `<td style="border: 1px solid #ddd; padding: 8px;">${detail || ''}</td>`).join('')}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                `;
                
                console.log('Subtable row added successfully');
            } else {
                console.error('New row was not found after addition.');
            }
        } catch (error) {
            console.error(`Error fetching row data from ${dataSourceType}:`, error);
            
            // Show error message in expanded row
            const errorRowData = {
                id: `error-${row.getData().id}`,
                _isSubtable: true,
                _error: true,
                _errorMessage: error.message
            };
            
            const newRow = await row.getTable().addRow(errorRowData, false, row, true);
            if (newRow) {
                const newRowElement = newRow.getElement();
                newRowElement.classList.add('subtable');
                
                // Replace the entire row content with our error message
                newRowElement.innerHTML = `
                    <div class="tabulator-cell subtable-cell" style="padding: 0; border: none; background-color: #ffffff; width: 100%;">
                        <div style="padding: 20px; text-align: center; color: #d00; font-style: italic;">
                            Error loading data: ${error.message}
                        </div>
                    </div>
                `;
            }
        }
    }, [dataSourceType, options]);
console.log("toggleRow", toggleRow);
    return { toggleRow };
};
